import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import AppLayout from '../../../component/appLayout';
import { Group, FileDetails, DropOverlay, DropMessage, DropTitle, DropText } from './styles';
import Accounting from '../../../component/accounting';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import { useHistory, useParams } from 'react-router-dom';
import { addFileData, getDocumentData, getProjectFiles, updateFileData } from '../../../services/projectServices';
import {
  extensionName,
  formatBytes,
  getFirebaseUrlsFromFiles,
  getUrlParam,
  isEmpty,
  handleFileRead
} from '../../../helpers/common';
import { LinkModalInterface } from '../../../interfaces/ProjectInterface';
import { FILES_LIST_TYPE, FILE_TYPES, LINK_FILE_DATA, EXTERNAL_LINK_TYPES } from '../../../global/constants';
import FilesBreadcrumb from './filesBreadcrumb';
import FilesEmptyState from './filesEmptyState';
import FileLoading from '../../../component/loading/fileLoading';
import FileListView from '../../../component/fileListView';
import FileGrid from './fileGridView';
import FilesDropdownContent from './filesDropdownContent';
import Modal from '../../../component/modals/modal';
import DeleteModal from '../../../component/modals/deleteModal';
import { captureException } from '../../../services/logService';
import FilesModal from '../../../component/modals/filesModal';
import MoveFolderModal from './moveFolderModal';
import FileUploadCard from '../../../component/fileUploadCard';
import { rootStore } from '../../../mobx/rootStore';
import { RawProjectFile } from '../../../mobx/interfaces/project';
import { observer } from 'mobx-react-lite';
import BottomSubNavigation from '../../../component/bottomNavigation/subNavigation';
import { dispatchAppError, dispatchAppSuccess } from '../../../services/appService';
import LinkFileModal from '../../../component/modals/linkFileModal';
import CreateFilesDropdown from '../../../component/dropdowns/createFilesDropdown';
import { ROUTES } from '../../../routes/urls';
import { deleteFileFromFirebase } from '../../../helpers/firebaseHelper';

// Interface for file data during upload
interface UploadFileData {
  file: File;
  fileContent: string;
  fileType: string;
  name: string;
  size: number;
}

// Opens an external URL in a new tab with error handling
const openExternalUrl = (url?: string): void => {
  try {
    if (!url) throw new Error('Invalid URL');
    const parsed = new URL(url);
    window.open(parsed.toString(), '_blank')?.focus();
  } catch (error) {
    captureException(error);
    rootStore.alertNotificationStore.setErrorMessage('Can not open this URL file.');
  }
};

// Navigate to document editor/view. Kept as a module helper to avoid duplicating logic.
const handleDocNavigation = async (
  item: RawProjectFile,
  historyRef: ReturnType<typeof useHistory>,
  folderIdParam?: string | null,
  projectId?: string
): Promise<void> => {
  await getDocumentData(item?.id);
  const projectIdStr = projectId ?? '';
  if (folderIdParam && !isEmpty(item?.id)) {
    const docIdStr = item?.id ?? '';
    historyRef.push(
      `${ROUTES.PROJECT_DOCS.replace(':id', projectIdStr).replace(':docid', docIdStr)}?folder=${folderIdParam}`
    );
  } else if (!isEmpty(item?.id) && !folderIdParam) {
    const docIdStr = item?.id ?? '';
    historyRef.push(ROUTES.PROJECT_DOCS.replace(':id', projectIdStr).replace(':docid', docIdStr));
  } else if (folderIdParam && isEmpty(item?.id)) {
    historyRef.push(
      `${ROUTES.PROJECT_DOCS.replace(':id', projectIdStr).replace(':docid', 'new')}?folder=${folderIdParam}`
    );
  } else {
    historyRef.push(ROUTES.PROJECT_DOCS.replace(':id', projectIdStr).replace(':docid', 'new'));
  }
};

const Files: React.FC = () => {
  //Ref
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Tracks nested dragenter/dragleave events so the overlay does not flicker
  const dragCounter = useRef(0);

  // States
  const [isDragActive, setIsDragActive] = useState(false);
  const [path, setPath] = useState<RawProjectFile[]>([]);
  const [currentItem, setCurrentItem] = useState<RawProjectFile | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteModelOpen, setDeleteModelOpen] = useState(false);
  const [isUploadFile, setUploadFile] = useState(false);
  const [uploadFileDetail, setUploadedFileDetail] = useState<UploadFileData[]>();
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isFileModalOpen, setIsFileModelOpen] = useState(false);
  const [modalData, setModalData] = useState<LinkModalInterface>();
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);

  //Mobx store variables
  const { workspaceStore, projectStore } = rootStore;
  const { currentWorkspace: workspace } = workspaceStore;
  const { currentProject, filesViewListType, uploadFilePercent, loading } = projectStore;

  // Other variables
  const params: { id: string } = useParams();
  const history = useHistory();
  const folderId = getUrlParam(history.location.search, 'folder');
  const uploadFileLength = uploadFileDetail?.length || 0;

  // load initial data
  const loadData = useCallback(async () => {
    if (!isEmpty(workspace?.id)) {
      try {
        const result = await getProjectFiles(params?.id);
        if (folderId) {
          currentProject?.setCurrentFolderId(folderId);
          let pathRef: RawProjectFile[] = [];
          let ref = result?.find((x: { id: string }) => x?.id === folderId);
          pathRef.push(ref as RawProjectFile);
          while (!isEmpty(ref?.parentId)) {
            const currentRef = ref;
            ref = result?.find((x: { id: string }) => x?.id === currentRef?.parentId);
            if (ref) {
              pathRef.push(ref as RawProjectFile);
            } else {
              return;
            }
          }
          setPath(pathRef);
        } else {
          currentProject?.setCurrentFolderId(null);
        }
      } catch (error) {
        captureException(error);
      }
    }
  }, [workspace?.id, params?.id, folderId, currentProject]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    return () => {
      currentProject?.setCurrentFolderId(null);
    };
  }, [currentProject]);

  // Prevent the browser's default behaviour of opening/navigating to a file
  // when it is dropped anywhere outside the dedicated drop zone (e.g. the
  // sidebar). Real uploads are still handled only by the Group's onDrop, which
  // stops propagation so these window listeners never fire for in-zone drops.
  useEffect(() => {
    const preventFileNavigation = (event: DragEvent) => {
      if (!Array.from(event.dataTransfer?.types || []).includes('Files')) return;
      event.preventDefault();
    };
    window.addEventListener('dragover', preventFileNavigation);
    window.addEventListener('drop', preventFileNavigation);
    return () => {
      window.removeEventListener('dragover', preventFileNavigation);
      window.removeEventListener('drop', preventFileNavigation);
    };
  }, []);

  // manage file click functionality
  const onClickFile = useCallback(
    async (item: RawProjectFile) => {
      switch (item?.uploadFileType) {
        case FILE_TYPES.FOLDER:
          currentProject?.setCurrentFolderId(item?.id);
          history.push(`${history.location.pathname}?folder=${item?.id}`);
          break;
        case FILE_TYPES.UPLOAD:
          history.push(`${history.location.pathname}/file-view/${item?.id}`);
          break;
        case FILE_TYPES.DOCS:
        case FILE_TYPES.WHITEBOARD:
          await handleDocNavigation(item, history, folderId, params?.id);
          break;
        default:
          if (EXTERNAL_LINK_TYPES.has(item?.uploadFileType)) {
            openExternalUrl(item?.url);
          }
          break;
      }
    },
    [currentProject, history, folderId, params?.id]
  );

  // redirect to files page
  const onClickFilesText = useCallback(() => {
    currentProject?.setCurrentFolderId(null);
    history.push(`${history.location.pathname}`);
  }, [currentProject, history]);

  // for open modal
  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  // for cancel model
  const onModalCancel = useCallback(() => {
    setIsModalOpen(false);
    setCurrentItem(null);
  }, []);

  // for open delete model
  const openDeleteModel = useCallback(() => {
    setDeleteModelOpen(true);
  }, []);

  // for close delete model
  const closeDeleteModel = useCallback(() => {
    setDeleteModelOpen(false);
    setCurrentItem(null);
  }, []);

  // for delete file
  const deleteFile = useCallback(
    async (e: React.SyntheticEvent) => {
      e.preventDefault();
      try {
        setDeleteLoading(true);
        // Uploaded files are stored in Firebase, so remove them from there too.
        if (currentItem?.uploadFileType === FILE_TYPES.UPLOAD && !isEmpty(currentItem?.url)) {
          await deleteFileFromFirebase(currentItem.url);
        }
        const payload = {
          IsArchived: true
        };
        const result = await updateFileData(currentItem?.id, payload);
        setDeleteModelOpen(false);
        if (result) {
          loadData();
        }
      } catch (error) {
        captureException(error);
      } finally {
        setDeleteLoading(false);
      }
    },
    [currentItem?.uploadFileType, currentItem?.url, currentItem?.id, loadData]
  );

  // for cancel model
  const cancelModel = useCallback(() => {
    setIsFileModelOpen(false);
    setCurrentItem(null);
  }, []);

  // for rename file name
  const onClickRename = useCallback(() => {
    setIsFileModelOpen(true);
  }, []);

  const openFileModal = useCallback((data: LinkModalInterface) => {
    setModalData(data);
    setIsFileModelOpen(true);
  }, []);
  const openLinkFileModal = useCallback((data: LinkModalInterface) => {
    setModalData(data);
    setIsLinkModalOpen(true);
  }, []);

  const closeLinkFileModal = useCallback(() => {
    setIsLinkModalOpen(false);
  }, []);
  const handleFileClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  // Stable callbacks for dropdown actions to avoid inline arrow functions
  const openDocsModal = useCallback(() => openFileModal(LINK_FILE_DATA.DOCS), [openFileModal]);
  const openWhiteboardModal = useCallback(() => openFileModal(LINK_FILE_DATA.WHITEBOARD), [openFileModal]);
  const openFolderModal = useCallback(() => openFileModal(LINK_FILE_DATA.FOLDER), [openFileModal]);
  const openDriveModal = useCallback(() => openLinkFileModal(LINK_FILE_DATA.DRIVE), [openLinkFileModal]);
  const openDropboxModal = useCallback(() => openLinkFileModal(LINK_FILE_DATA.DROPBOX), [openLinkFileModal]);
  const openOnedriveModal = useCallback(() => openLinkFileModal(LINK_FILE_DATA.ONEDRIVE), [openLinkFileModal]);
  const openNotionModal = useCallback(() => openLinkFileModal(LINK_FILE_DATA.NOTION), [openLinkFileModal]);
  const openFigmaModal = useCallback(() => openLinkFileModal(LINK_FILE_DATA.FIGMA), [openLinkFileModal]);
  // Shared upload routine used by both the file input and drag-and-drop.
  // Reads each file, uploads it to Firebase and registers it against the
  // current project/folder so dropped files land in the folder being viewed.
  const uploadFiles = useCallback(
    async (filesArray: File[]) => {
      if (isEmpty(filesArray)) return;
      try {
        setUploadFile(true);
        // Use handleFileRead for each file to prepare file data
        const filePromises = filesArray.map((file) => handleFileRead(file));
        const fileDataResults = await Promise.all(filePromises);
        // Filter out any undefined results (failed file reads)
        const fileDataArray = fileDataResults.filter((fileData): fileData is UploadFileData => !!fileData);

        setUploadedFileDetail(fileDataArray);

        if (!isEmpty(fileDataArray)) {
          // Save files inside the project's own folder when we know the project id.
          const uploadFolder = currentProject?.id ? `projects/${currentProject.id}/files/` : 'files/';
          const fileUrls = await getFirebaseUrlsFromFiles(fileDataArray, uploadFolder);
          const payloads = fileUrls?.map((fileUrl, index) => ({
            FileType: fileDataArray[index]?.fileType,
            IsSystemFile: true,
            Name: fileDataArray[index]?.name,
            ParentId: folderId ? folderId : null,
            ProjectId: params?.id,
            Size: Number(fileDataArray[index]?.size),
            UploadFileType: FILE_TYPES.UPLOAD,
            Url: fileUrl
          }));
          // Dispatch each payload
          for (const payload of payloads) {
            await addFileData(payload);
          }
          setUploadFile(false);
          setUploadedFileDetail(fileDataArray);
          dispatchAppSuccess('File uploaded successfully!');
          loadData();
        }
      } catch (error) {
        captureException(error);
        dispatchAppError('Error uploading files');
      } finally {
        setUploadFile(false);
      }
    },
    [folderId, loadData, params?.id, currentProject?.id]
  );

  const onFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files) return;
      await uploadFiles(Array.from(files));
      event.target.value = '';
    },
    [uploadFiles]
  );

  // Only react to OS file drags (ignore internal element drags)
  const isFileDrag = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => Array.from(event.dataTransfer?.types || []).includes('Files'),
    []
  );

  const handleDragEnter = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (!isFileDrag(event)) return;
      event.preventDefault();
      event.stopPropagation();
      dragCounter.current += 1;
      setIsDragActive(true);
    },
    [isFileDrag]
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (!isFileDrag(event)) return;
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = 'copy';
    },
    [isFileDrag]
  );

  const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    async (event: React.DragEvent<HTMLDivElement>) => {
      if (!isFileDrag(event)) return;
      event.preventDefault();
      event.stopPropagation();
      dragCounter.current = 0;
      setIsDragActive(false);
      const droppedFiles = event.dataTransfer?.files;
      if (!droppedFiles || droppedFiles.length === 0) return;
      await uploadFiles(Array.from(droppedFiles));
    },
    [isFileDrag, uploadFiles]
  );

  const dropdownContent = useMemo(
    () => (
      <FilesDropdownContent
        onFileUpload={onFileUpload}
        fileInputRef={fileInputRef}
        openDocsModal={openDocsModal}
        openWhiteboardModal={openWhiteboardModal}
        openFolderModal={openFolderModal}
        openDriveModal={openDriveModal}
        openDropboxModal={openDropboxModal}
        openOnedriveModal={openOnedriveModal}
        openNotionModal={openNotionModal}
        openFigmaModal={openFigmaModal}
        handleFileClick={handleFileClick}
      />
    ),
    [
      onFileUpload,
      openDocsModal,
      openWhiteboardModal,
      openFolderModal,
      openDriveModal,
      openDropboxModal,
      openOnedriveModal,
      openNotionModal,
      openFigmaModal,
      handleFileClick
    ]
  );

  return (
    <>
      <AppLayout isOverflowHidden={isDragActive}>
        <Group
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}>
          <Accounting renderDropdownItems={dropdownContent} />

          <FileDetails>
            {!isEmpty(folderId) && path?.length > 0 && (
              <FilesBreadcrumb path={path} onClickFilesText={onClickFilesText} onClickFile={onClickFile} />
            )}
            {loading && currentProject?.folderWiseFiles && currentProject?.folderWiseFiles?.length === 0 && (
              <FileLoading />
            )}
            {!loading && currentProject?.folderWiseFiles?.length === 0 && <FilesEmptyState />}
            {currentProject?.folderWiseFiles &&
              currentProject?.folderWiseFiles?.length > 0 &&
              filesViewListType !== FILES_LIST_TYPE.LIST && (
                <FileGrid
                  files={currentProject?.folderWiseFiles || []}
                  currentItem={currentItem}
                  setCurrentItem={setCurrentItem}
                  onClickFile={onClickFile}
                  onClickRename={onClickRename}
                  openModal={openModal}
                  openDeleteModel={openDeleteModel}
                />
              )}
            {currentProject?.folderWiseFiles &&
              currentProject?.folderWiseFiles?.length > 0 &&
              filesViewListType === FILES_LIST_TYPE.LIST && (
                <FileListView
                  filesList={currentProject?.folderWiseFiles}
                  setSelectedItem={setCurrentItem}
                  onClickFile={onClickFile}
                  onClickRename={onClickRename}
                  OpenModal={openModal}
                  Opendeletemodel={openDeleteModel}
                  selectedItem={currentItem}
                />
              )}
          </FileDetails>
          {isDragActive && (
            <DropOverlay>
              <DropMessage>
                <SVGIcon name='updated-file-icon' width='32' height='32' viewBox='0 0 18 18' />
                <DropTitle>Drop files to upload</DropTitle>
                <DropText>
                  {folderId ? 'Files will be added to this folder' : 'Files will be added to this project'}
                </DropText>
              </DropMessage>
            </DropOverlay>
          )}
        </Group>
        {isUploadFile && uploadFilePercent > 0 && uploadFileDetail && (
          <FileUploadCard
            fileName={uploadFileDetail?.[0]?.name}
            extension={extensionName(uploadFileDetail?.[0]).toLowerCase()}
            progress={uploadFilePercent}
            fileSize={
              uploadFileLength > 1 ? `& ${uploadFileLength - 1} others` : formatBytes(uploadFileDetail?.[0]?.size || 0)
            }
          />
        )}
      </AppLayout>
      <Modal open={isFileModalOpen} onClose={cancelModel} maxWidth={'462px'}>
        <FilesModal
          onClose={cancelModel}
          modalData={modalData}
          itemData={currentItem}
          folderId={folderId}
          loadFilesData={loadData}
        />
      </Modal>
      <Modal open={isLinkModalOpen} onClose={closeLinkFileModal} maxWidth={'462px'}>
        <LinkFileModal onClose={closeLinkFileModal} modalData={modalData} loadFilesData={loadData} accounting={true} />
      </Modal>
      <Modal open={isModalOpen} onClose={onModalCancel} maxWidth={'462px'}>
        <MoveFolderModal onModalCancel={onModalCancel} loadData={loadData} currentItem={currentItem} />
      </Modal>
      <Modal open={deleteModelOpen} onClose={closeDeleteModel} maxWidth={'334px'}>
        <DeleteModal
          onClose={closeDeleteModel}
          onOk={deleteFile}
          loading={deleteLoading}
          modalTitle={`Delete ${currentItem?.fileType === String(FILE_TYPES.FOLDER) ? 'Folder?' : 'File?'}`}
          description={`Are you sure you want to delete this ${
            currentItem?.fileType === String(FILE_TYPES.FOLDER) ? 'Folder?' : 'File?'
          }`}
        />
      </Modal>
      {/* Bottom Navigation */}
      <BottomSubNavigation
        backNavigationPath='/home'
        rightElement={<CreateFilesDropdown renderDropdownItems={dropdownContent} onFileUpload={onFileUpload} />}
      />
    </>
  );
};

export default observer(Files);
